/**
 * Platform-agnostic toast service.
 *
 * The legacy `toast(msg, type)` from index.html mutated `#toast` directly.
 * We can't ship the same approach into a React tree, so this module is split
 * in two:
 *
 *   - `toast(msg, type?)` — fire-and-forget API used everywhere.
 *   - `subscribeToasts(handler)` — UI layer (a React provider, a vanilla DOM
 *     adapter, or a test) attaches once on boot and renders whatever it
 *     wants.
 *
 * Behavior parity with legacy: types are 'success' | 'error' | '' (default
 * amber). Each new toast supersedes the previous one — the UI is single-slot.
 */

export type ToastType = 'success' | 'error' | ''

export interface ToastEvent {
  msg: string
  type: ToastType
}

type Handler = (event: ToastEvent) => void

const handlers = new Set<Handler>()

export function toast(msg: string, type: ToastType = ''): void {
  const event: ToastEvent = { msg, type }
  for (const h of handlers) h(event)
}

/** Subscribe a handler. Returns an unsubscribe function. */
export function subscribeToasts(handler: Handler): () => void {
  handlers.add(handler)
  return () => {
    handlers.delete(handler)
  }
}

/** Test-only helper: clears all subscribers. */
export function _resetToastsForTests(): void {
  handlers.clear()
  hostBridgeOff = null
}

type HostToast = (msg: string, type?: ToastType) => void

let hostBridgeOff: (() => void) | null = null

/**
 * Route every `toast()` to the legacy host's visible `window.toast()`.
 * `entry.ts` installs this once at boot so islands' toasts show up in
 * `crm/index.html`. The host function is looked up per call, so it works
 * even if the host defines it after the islands bundle loads. Idempotent;
 * returns an uninstall function.
 */
export function installHostToastBridge(): () => void {
  if (!hostBridgeOff) {
    const off = subscribeToasts(({ msg, type }) => {
      const host = (window as Window & { toast?: HostToast }).toast
      if (typeof host === 'function') host(msg, type)
    })
    hostBridgeOff = () => {
      off()
      hostBridgeOff = null
    }
  }
  return hostBridgeOff
}
